import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  Stack,
  Paper,
  Container,
  Alert,
  LinearProgress,
  Fade,
  Slide,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import toast, { Toaster } from "react-hot-toast";

import { useGlobalContext } from "../operations/globalContext";
import {
  getAccessTokenAPI,
  adminLoginAPI,
  generateOtpAPI,
  verifyOtpAPI,
} from "../operations/authService";

import { decryptJWT, decryptData } from "../helpers";

/* ---------------- JWT TYPE ---------------- */



/* ---------------- COMPONENT ---------------- */

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOTP] = useState("");
  const [userId, setUserId] = useState("");
  const [showOTPField, setShowOTPField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [otpTimerExpired, setOtpTimerExpired] = useState(false);
  const [timer, setTimer] = useState(60);
  const navigate = useNavigate();
  const { setUserDetails, accessJWT, setAccessJWT } = useGlobalContext();
  const [showPassword, setShowPassword] = useState(false);

  // Fetch access token on component mount
  useEffect(() => {
    async function getAccessToken() {
      let success = false;
      let count = 0;
      while (!success && count <= 4) {
        try {
          const response = await getAccessTokenAPI();
          console.log("=== Access Token Response Debug ===");
          console.log("Raw response:", response);
          console.log("Response type:", typeof response);

          // Try to safely access properties
          const statusCode = response?.statusCode || response?.status;
          const data = response?.data;

          console.log("Extracted statusCode:", statusCode, typeof statusCode);
          console.log("Extracted data:", data ? "exists (length: " + data.length + ")" : "missing");
          console.log("statusCode check:", statusCode, "==", 200, "=>", statusCode == 200);

          if (response && statusCode == 200 && data) {
            try {
              // Decrypt the encrypted JWT token from the response
              console.log("Attempting to decrypt JWT...");
              const decryptedJWT = decryptData(data);
              console.log("JWT decrypted successfully:", decryptedJWT?.substring(0, 50) + "...");
              setAccessJWT(decryptedJWT);
              success = true;
              console.log("Access JWT set successfully!");
            } catch (decryptErr) {
              console.error("Error decrypting JWT:", decryptErr);
              throw decryptErr;
            }
          } else {
            console.error("Validation failed:");
            console.error("  - response is object:", typeof response === 'object' && response !== null);
            console.error("  - statusCode value:", statusCode);
            console.error("  - statusCode == 200:", statusCode == 200);
            console.error("  - data exists:", !!data);
            console.error("  - data type:", typeof data);
          }
        } catch (err) {
          console.error("Error in getAccessToken:", err);
          console.error("Error stack:", err.stack);
        }
        count++;
      }
      if (!success) {
        console.error("Failed to get access token after", count, "attempts");
      }
    }
    if (!accessJWT) getAccessToken();
  }, [accessJWT, setAccessJWT]);

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const shouldRemember = localStorage.getItem("rememberMe");

    if (rememberedEmail && shouldRemember === "true") {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // OTP timer function
  const startOtpTimer = () => {
    setOtpTimerExpired(false);
    setTimer(60);
    const interval = setInterval(() => {
      setTimer((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setOtpTimerExpired(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  // Handle login (email + password)
  const handleLogin = async () => {
    setError("");

    // Validation
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Check if accessJWT is available
    if (!accessJWT) {
      setError("System initialization in progress. Please wait a moment and try again.");
      return;
    }

    try {
      setLoading(true);
      console.log("Calling adminLoginAPI with:", { email, accessJWT: accessJWT ? "present" : "missing" });
      const loginRes = await adminLoginAPI({ email, password }, accessJWT);
      console.log("loginRes----->", loginRes);

      // Check if server returned an error
      if (loginRes?.error !== undefined) {
        console.error("Server error response:", loginRes);
        setError("Server error occurred. Please try again later or contact support.");
        return;
      }

      // Check for 500 status code
      if (loginRes?.statusCode === 500) {
        setError("Server error occurred. Please try again later.");
        return;
      }

      console.log("Checking login response...");
      console.log("  loginRes.statusCode:", loginRes?.statusCode);
      console.log("  loginRes.data:", loginRes?.data);
      console.log("  Condition result:", loginRes?.statusCode === 200 && loginRes?.data);

      if (loginRes?.statusCode === 200 && loginRes?.data) {
        const token = loginRes.data?.jwtToken;
        console.log("Received encrypted JWT token:", token?.substring(0, 50) + "...");

        if (!token) {
          setError("Invalid response from server.");
          return;
        }

        try {
          // Decrypt the JWT token
          console.log("Decrypting JWT token...");
          let decryptedToken = decryptData(token);
          console.log("Decrypted token:", decryptedToken?.substring(0, 100));
          console.log("Decrypted token type:", typeof decryptedToken);

          // Check if the token is double-stringified (has quotes around it)
          if (typeof decryptedToken === 'string' && decryptedToken.startsWith('"') && decryptedToken.endsWith('"')) {
            console.log("Token is quoted, removing quotes...");
            decryptedToken = decryptedToken.slice(1, -1);
            console.log("Token after removing quotes:", decryptedToken?.substring(0, 100));
          }

          // Decode the JWT to get user data
          console.log("Decoding JWT to extract user ID...");
          const decoded = jwtDecode(decryptedToken);
          console.log("Decoded JWT:", decoded);
          const uid = decoded?.id;
          console.log("Extracted user ID:", uid);

          if (!uid) {
            setError("User ID not found in token.");
            return;
          }

          // Handle Remember Me (only store email, never password)
          if (rememberMe) {
            localStorage.setItem("rememberedEmail", email);
            localStorage.setItem("rememberMe", "true");
          } else {
            localStorage.removeItem("rememberedEmail");
            localStorage.removeItem("rememberMe");
          }

          setUserId(String(uid));
          localStorage.setItem("userId", String(uid));
          setShowOTPField(true);
          startOtpTimer();
          toast.success("OTP sent successfully!");
        } catch (decryptError) {
          console.error("Token decryption/decode error:", decryptError);
          setError("Authentication failed. Please try again.");
        }
      } else {
        setError(loginRes?.message || "Invalid email or password.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err?.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Generate New OTP
  const handleGenerateNewOTP = async () => {
    setError("");
    setOTP("");

    if (!userId) {
      setError("User ID missing. Please log in again.");
      return;
    }

    try {
      setLoading(true);
      const otpRes = await generateOtpAPI({ email, userId }, accessJWT);

      if (otpRes?.statusCode === 200) {
        startOtpTimer();
        toast.success("New OTP sent successfully!");
      } else {
        setError(otpRes?.message || "Failed to send OTP.");
      }
    } catch (err) {
      console.error("OTP generation error:", err);
      setError(err?.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP Verification
  const handleVerifyOtp = async () => {
    setError("");

    // Validation
    if (!otp) {
      setError("Please enter the OTP.");
      return;
    }

    if (otp.length !== 6) {
      setError("OTP must be 6 digits.");
      return;
    }

    const storedUserId = localStorage.getItem("userId");
    if (!storedUserId) {
      setError("User ID missing. Please log in again.");
      return;
    }

    try {
      setLoading(true);
      const response = await verifyOtpAPI(
        { userId: storedUserId, otp },
        accessJWT
      );

      if (response?.statusCode === 200 && response?.data?.jwtToken) {
        const encryptedJwtToken = response.data.jwtToken;
        console.log("Received encrypted JWT token from OTP verification:", encryptedJwtToken?.substring(0, 50) + "...");

        try {
          // Decrypt the JWT token
          console.log("Decrypting JWT token from OTP verification...");
          let decryptedJwtToken = decryptData(encryptedJwtToken);
          console.log("Decrypted token:", decryptedJwtToken?.substring(0, 100));
          console.log("Decrypted token type:", typeof decryptedJwtToken);

          // Check if the token is double-stringified (has quotes around it)
          if (typeof decryptedJwtToken === 'string' && decryptedJwtToken.startsWith('"') && decryptedJwtToken.endsWith('"')) {
            console.log("Token is quoted, removing quotes...");
            decryptedJwtToken = decryptedJwtToken.slice(1, -1);
            console.log("Token after removing quotes:", decryptedJwtToken?.substring(0, 100));
          }

          // Store the decrypted token
          localStorage.setItem("jwtToken", decryptedJwtToken);

          // Decode the JWT to get user details
          console.log("Decoding JWT token...");
          const decodedToken = jwtDecode(decryptedJwtToken);
          console.log("Decoded token:", decodedToken);
          localStorage.setItem("userDetails", JSON.stringify(decodedToken));

          // Role validation
          const allowedRoles = [1, 2, 3, 4];
          console.log("Validating role:", decodedToken?.role);
          if (!allowedRoles.includes(decodedToken?.role)) {
            console.error("Unauthorized role:", decodedToken?.role);
            navigate("/auth/login");
            localStorage.removeItem("jwtToken");
            localStorage.removeItem("userDetails");
            localStorage.removeItem("userId");
            setUserDetails({});
            toast.error("Invalid login credentials - unauthorized role");
            return;
          }

          console.log("Role validated successfully, setting user details...");
          setUserDetails(decodedToken);
          toast.success("Login successful!");

          // Navigate to dashboard
          console.log("Navigating to /...");
          navigate("/");
        } catch (decryptError) {
          console.error("Token decryption/decode error:", decryptError);
          console.error("Error stack:", decryptError?.stack);
          setError("Authentication failed. Please try again.");
        }
      } else {
        setError(response?.message || "OTP verification failed. Please try again.");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError(err?.response?.data?.message || "OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!showOTPField) {
      await handleLogin();
    } else {
      await handleVerifyOtp();
    }
  };

  // Button text based on state
  const renderSubmitButtonText = () => {
    if (loading) return "Processing...";
    return showOTPField ? "Verify OTP" : "Log in";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Toaster position="top-right" />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
            }}
          >
            {loading && <LinearProgress />}

            <Box sx={{ p: { xs: 3, sm: 5 } }}>
              {/* Header Section */}
              <Fade in={true} timeout={1000}>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 1,
                    }}
                  >
                    {showOTPField ? "Verify OTP" : "Welcome Back"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {showOTPField
                      ? "Enter the 6-digit code sent to your email"
                      : "Sign in to access your admin dashboard"}
                  </Typography>
                </Box>
              </Fade>

              {/* Error Alert */}
              {error && (
                <Fade in={true}>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Login Form */}
              <form onSubmit={handleSignIn}>
                <Stack spacing={3}>
                  {!showOTPField ? (
                    <>
                      {/* Email Field */}
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      {/* Password Field */}
                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      {/* Remember Me */}
                      <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <MuiCheckbox
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              sx={{
                                color: "#667eea",
                                "&.Mui-checked": {
                                  color: "#667eea",
                                },
                              }}
                            />
                          }
                          label={<Typography variant="body2">Remember me</Typography>}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* OTP Field */}
                      <TextField
                        fullWidth
                        label="Enter OTP"
                        type="text"
                        value={otp}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 6) {
                            setOTP(value);
                          }
                        }}
                        required
                        inputProps={{ maxLength: 6 }}
                        autoComplete="one-time-code"
                        placeholder="000000"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyOutlinedIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                          "& input": {
                            fontSize: "1.5rem",
                            letterSpacing: "0.5rem",
                            textAlign: "center",
                          },
                        }}
                      />

                      {/* OTP Timer */}
                      {!otpTimerExpired && timer > 0 && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Resend OTP in <strong>{timer}</strong> seconds ⏱️
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(timer / 60) * 100}
                            sx={{
                              mt: 1,
                              borderRadius: 1,
                              backgroundColor: "rgba(102, 126, 234, 0.1)",
                              "& .MuiLinearProgress-bar": {
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                              },
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || (showOTPField && otpTimerExpired)}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                      "&.Mui-disabled": {
                        background: "#ccc",
                        color: "#888",
                      },
                    }}
                  >
                    {renderSubmitButtonText()}
                  </Button>

                  {/* Resend OTP Button */}
                  {showOTPField && otpTimerExpired && (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleGenerateNewOTP}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
                          boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                          transform: "translateY(-2px)",
                        },
                        "&:active": {
                          transform: "translateY(0)",
                        },
                        "&.Mui-disabled": {
                          background: "#ccc",
                          color: "#888",
                        },
                      }}
                    >
                      {loading ? "Sending..." : "Resend OTP"}
                    </Button>
                  )}
                </Stack>
              </form>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                py: 2,
                px: 3,
                background: "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                borderTop: "1px solid rgba(102, 126, 234, 0.1)",
              }}
            >
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                © 2024 Zenvoya. All rights reserved.
              </Typography>
            </Box>
          </Paper>
      </Container>
    </Box>
  );
}
